// Revenue (SM-714 / SM-708). The node's own revenue in the selected
// currency, by state — pending is NOT available: it becomes available
// when broker-service's revenue reconciliation has matched it (the
// sentence and the latest run come from /revenue/periods) — then today
// (the Asia/Bangkok day), the current calendar month and the previous one
// (UTC) split maker / taker / redemption, and the earnings list.
//
//   GET /api/broker/{id}/revenue/balances                 RevenueBalancesResponse (one row per currency)
//   GET /api/broker/{id}/revenue/periods?currency=        RevenuePeriodsResponse
//   GET /api/broker/{id}/revenue/earnings?currency=&state=&role=&offset=&limit=
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Money } from "@/components/ui/Money";
import { Id, Pager, Stat, TableFrame, Time } from "@/components/ui/Common";
import { EmptyState, QueryBlock } from "@/components/ui/States";
import { Select } from "@/components/ui/Field";
import { fetchEarnings, fetchRevenueBalances, fetchRevenuePeriods, keys } from "@/lib/queries";
import { formatBps } from "@/lib/units";
import { EARNING_STATES, REVENUE_ROLES, type RevenueBalance, type RevenuePeriodFigures, type RevenuePeriodsResponse } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf } from "@/i18n/labels";

export const Route = createFileRoute("/_app/revenue")({
  beforeLoad: capabilityGuard("FINANCE_VIEW"),
  component: RevenuePage,
});

function RevenuePage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const { nodeId, currency } = useScope();
  const probe = { queryKey: keys.revenuePeriods(nodeId, currency), queryFn: () => fetchRevenuePeriods(nodeId, currency) };
  const periodsQ = useQuery({ ...probe, enabled: guard.kind === "allow" });
  const balancesQ = useQuery({
    queryKey: keys.revenueBalances(nodeId),
    queryFn: () => fetchRevenueBalances(nodeId),
    enabled: guard.kind === "allow",
  });
  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader title={t("revenue.title")} subtitle={t("revenue.subtitle")} coverage="node" money />
      <div className="space-y-4">
        <SectionCard title={t("revenue.balances")}>
          <QueryBlock query={balancesQ}>
            {(d) => {
              const row = (d.balances ?? []).find((b) => b.currency === currency);
              return row ? <Balances b={row} /> : <EmptyState title={t("revenue.noBalance", { currency })} />;
            }}
          </QueryBlock>
          <QueryBlock query={periodsQ} lines={1}>
            {(p) => <ReconSentence p={p} />}
          </QueryBlock>
        </SectionCard>
        <SectionCard title={t("revenue.periods")}>
          <QueryBlock query={periodsQ} lines={4}>
            {(p) => <Periods p={p} />}
          </QueryBlock>
        </SectionCard>
        {/* keyed by node and currency: a switch starts at the first page with no filter (SM-943) */}
        <Earnings key={`${nodeId}|${currency}`} />
      </div>
    </Guarded>
  );
}

function Balances({ b }: { b: RevenueBalance }) {
  const { t } = useI18n();
  const c = b.currency;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      <Stat label={t("revenue.state.pending")} sub={t("revenue.pendingSub")}>
        <Money currency={c} value={b.pending} />
      </Stat>
      <Stat label={t("revenue.state.available")} sub={t("revenue.availableSub")}>
        <Money currency={c} value={b.available} />
      </Stat>
      <Stat label={t("revenue.state.held")} sub={t("revenue.heldSub")}>
        <Money currency={c} value={b.held} />
      </Stat>
      <Stat label={t("revenue.state.payoutReserved")} sub={t("revenue.reservedSub")}>
        <Money currency={c} value={b.payout_reserved} />
      </Stat>
      <Stat label={t("revenue.state.paid")}>
        <Money currency={c} value={b.paid} />
      </Stat>
      <Stat label={t("revenue.state.unpaid")} sub={t("revenue.unpaidSub")}>
        <Money currency={c} value={b.unpaid} />
      </Stat>
    </div>
  );
}

function ReconSentence({ p }: { p: RevenuePeriodsResponse }) {
  const { t } = useI18n();
  return (
    <div className="banner mt-4 mb-0 items-start text-xs" role="note">
      <span aria-hidden>ℹ</span>
      <div>
        <p className="text-t-1">{t("revenue.pendingExplained")}</p>
        {p.latest_recon_run_id ? (
          <p className="mt-1 text-t-2">
            {t("revenue.latestRecon", { state: labelOf(t, "reconState", p.latest_recon_state) })}{" "}
            <Time ms={p.latest_recon_at_unix_ms} /> · <Id value={p.latest_recon_run_id} />
          </p>
        ) : (
          <p className="mt-1 text-t-3">{t("revenue.noRecon")}</p>
        )}
      </div>
    </div>
  );
}

function Periods({ p }: { p: RevenuePeriodsResponse }) {
  const { t } = useI18n();
  const windows: { key: string; label: string; f: RevenuePeriodFigures }[] = [
    { key: "today", label: t("revenue.today"), f: p.today },
    { key: "current", label: t("revenue.current"), f: p.current },
    { key: "previous", label: t("revenue.previous"), f: p.previous },
  ];
  const c = p.currency;
  return (
    <>
      <TableFrame label={t("revenue.periods")}>
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">{t("revenue.window")}</th>
              <th scope="col">{t("revenue.kind")}</th>
              <th scope="col" className="text-right">
                {t("revenueRole.maker")}
              </th>
              <th scope="col" className="text-right">
                {t("revenueRole.taker")}
              </th>
              <th scope="col" className="text-right">
                {t("revenueRole.redemption")}
              </th>
              <th scope="col" className="text-right">
                {t("revenue.total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {windows.map((w) => (
              <Fragment key={w.key}>
                <tr>
                  <th scope="row" rowSpan={2} className="align-top font-medium text-t-1">
                    {w.label}
                    <span className="block text-2xs font-normal text-t-3">
                      <Time ms={w.f?.from_unix_ms} /> → <Time ms={w.f?.to_unix_ms} />
                    </span>
                  </th>
                  <td className="text-xs">{t("revenue.earned")}</td>
                  <td className="text-right">
                    <Money currency={c} value={w.f?.earned?.maker} />
                  </td>
                  <td className="text-right">
                    <Money currency={c} value={w.f?.earned?.taker} />
                  </td>
                  <td className="text-right">
                    <Money currency={c} value={w.f?.earned?.redemption} />
                  </td>
                  <td rowSpan={2} className="text-right align-top font-semibold">
                    <Money currency={c} value={w.f?.total} />
                  </td>
                </tr>
                <tr>
                  <td className="text-xs">{t("revenue.sharesReceived")}</td>
                  <td className="text-right">
                    <Money currency={c} value={w.f?.shares_received?.maker} />
                  </td>
                  <td className="text-right">
                    <Money currency={c} value={w.f?.shares_received?.taker} />
                  </td>
                  <td className="text-right">
                    <Money currency={c} value={w.f?.shares_received?.redemption} />
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </TableFrame>
      <p className="mt-2 text-2xs text-t-3">{t("revenue.periodsNote")}</p>
    </>
  );
}

function Earnings() {
  const { t } = useI18n();
  const { nodeId, currency } = useScope();
  const [state, setState] = useState("");
  const [role, setRole] = useState("");
  const [offset, setOffset] = useState(0);
  const params = { currency, state: state || undefined, role: role || undefined, offset, limit: 25 };
  const q = useQuery({ queryKey: keys.earnings(nodeId, params), queryFn: () => fetchEarnings(nodeId, params) });
  return (
    <SectionCard
      title={t("revenue.earnings")}
      actions={
        <div className="flex flex-wrap gap-2">
          <div className="w-40">
            <Select
              label={t("common.status")}
              value={state}
              onChange={(e) => {
                setState(e.target.value);
                setOffset(0);
              }}
              options={[{ value: "", label: t("common.all") }, ...EARNING_STATES.map((s) => ({ value: s, label: t(`earningState.${s}`) }))]}
            />
          </div>
          <div className="w-36">
            <Select
              label={t("revenue.role")}
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                setOffset(0);
              }}
              options={[
                { value: "", label: t("common.all") },
                ...REVENUE_ROLES.map((r) => ({ value: r, label: labelOf(t, "revenueRole", r.toLowerCase()) })),
              ]}
            />
          </div>
        </div>
      }
    >
      <QueryBlock query={q} empty={(d) => ((d.items ?? []).length === 0 && !d.page?.total ? <EmptyState title={t("revenue.noEarnings")} /> : null)}>
        {(d) => (
          <>
            {/* a page past the end (rows gone since it was opened) keeps the pager to step back */}
            {(d.items ?? []).length === 0 ? (
              <EmptyState title={t("revenue.noEarnings")} />
            ) : (
              <TableFrame label={t("revenue.earnings")}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th scope="col">{t("common.when")}</th>
                      <th scope="col">{t("revenue.origin")}</th>
                      <th scope="col">{t("revenue.role")}</th>
                      <th scope="col">{t("revenue.source")}</th>
                      <th scope="col" className="text-right">
                        {t("revenue.share")}
                      </th>
                      <th scope="col" className="text-right">
                        {t("revenue.amount")}
                      </th>
                      <th scope="col">{t("common.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.items ?? []).map((e) => (
                      <tr key={e.earning_id}>
                        <td className="text-xs">
                          <Time ms={e.created_at_unix_ms} />
                        </td>
                        <td className="text-xs">{labelOf(t, "earningOrigin", e.origin)}</td>
                        <td className="text-xs">{e.role ? labelOf(t, "revenueRole", e.role.toLowerCase()) : "—"}</td>
                        <td>
                          <Id value={e.source_id} />
                        </td>
                        <td className="num text-right">{e.share_bps ? formatBps(e.share_bps) : "—"}</td>
                        <td className="text-right">
                          <Money currency={e.currency} value={e.amount} tone="auto" />
                        </td>
                        <td>
                          <Badge tone={e.state === "AVAILABLE" ? "yes" : e.state === "VOID" ? "neutral" : "accent"}>
                            {labelOf(t, "earningState", e.state)}
                          </Badge>
                          {e.held && (
                            <Badge tone="warn" className="ml-1" title={e.hold_reason}>
                              {t("revenue.held")}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableFrame>
            )}
            <Pager page={d.page} onOffset={setOffset} />
          </>
        )}
      </QueryBlock>
    </SectionCard>
  );
}
